import { AmountYield, MixingWork } from '@twinsketch/topika-model';
import { useMemo } from 'react';
import { FormulationToMixingHelper } from '../../helper';
import { calculateActualUsageFromMixingWorks } from './materialMatching';

interface MaterialUsageComparisonProps {
  materialsNeeded: AmountYield[];
  mixingWorks: MixingWork[];
  activeMaterialKg: number;
  formulation: FormulationToMixingHelper;
}

interface MaterialUsage {
  materialName: string;
  planned: number;
  actual: number;
  difference: number;
  variancePercent: number;
}

const MaterialUsageComparison: React.FC<MaterialUsageComparisonProps> = ({
  materialsNeeded,
  mixingWorks,
  activeMaterialKg,
  formulation,
}) => {
  const materialComparison: MaterialUsage[] = useMemo(() => {
    const actualUsageData = calculateActualUsageFromMixingWorks(
      mixingWorks,
      materialsNeeded,
      formulation?.formulation?.type,
      formulation?.formulation?.elements
    );

    const comparison: MaterialUsage[] = [];

    // Filter out foil materials
    const filteredMaterialsNeeded = materialsNeeded.filter(
      planned => !planned.materialName.toLowerCase().includes('foil')
    );

    filteredMaterialsNeeded.forEach(planned => {
      const usageData = actualUsageData.get(planned.materialName);
      const actual = usageData?.totalAmount || 0;
      const difference = actual - planned.min;
      const variancePercent = planned.min > 0 ? (difference / planned.min) * 100 : 0;

      comparison.push({
        materialName: planned.materialName,
        planned: planned.min,
        actual,
        difference,
        variancePercent,
      });
    });

    return comparison.sort((a, b) => Math.abs(b.variancePercent) - Math.abs(a.variancePercent));
  }, [materialsNeeded, mixingWorks, formulation]);

  const totals = useMemo(() => {
    return materialComparison.reduce(
      (acc, m) => ({
        planned: acc.planned + m.planned,
        actual: acc.actual + m.actual,
        difference: acc.difference + m.difference,
      }),
      { planned: 0, actual: 0, difference: 0 }
    );
  }, [materialComparison]);

  const getVarianceStatus = (variance: number): string => {
    const abs = Math.abs(variance);
    if (abs < 5) return '✓';
    if (abs < 20) return '△';
    return '✗';
  };

  const getVarianceColor = (variance: number): string => {
    const abs = Math.abs(variance);
    if (abs < 5) return '#28a745';
    if (abs < 20) return '#ffc107';
    return '#dc3545';
  };

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ marginBottom: '12px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Material Usage Analysis</h3>
        <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
          Planned vs Actual · {mixingWorks.length} batches
          {activeMaterialKg && ` · Target: ${activeMaterialKg} kg`}
        </p>
      </div>

      <table className="mixing-table">
        <thead>
          <tr>
            <th style={{ width: '25%' }}>Material</th>
            <th style={{ width: '15%' }}>Planned (kg)</th>
            <th style={{ width: '15%' }}>Actual (kg)</th>
            <th style={{ width: '15%' }}>Diff (kg)</th>
            <th style={{ width: '15%' }}>Variance (%)</th>
            <th style={{ width: '15%' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {materialComparison.map(material => (
            <tr key={material.materialName}>
              <td style={{ textAlign: 'left', fontWeight: 500 }}>{material.materialName}</td>
              <td>{material.planned.toFixed(2)}</td>
              <td>{material.actual > 0 ? material.actual.toFixed(2) : '—'}</td>
              <td
                style={{
                  color:
                    material.actual > 0
                      ? material.difference >= 0
                        ? '#dc3545'
                        : '#28a745'
                      : '#666',
                  fontWeight: material.actual > 0 ? 600 : 400,
                }}
              >
                {material.actual > 0 ? (
                  <>
                    {material.difference > 0 ? '+' : ''}
                    {material.difference.toFixed(2)}
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td
                style={{
                  color:
                    material.actual > 0
                      ? material.variancePercent >= 0
                        ? '#dc3545'
                        : '#28a745'
                      : '#666',
                  fontWeight: material.actual > 0 ? 600 : 400,
                }}
              >
                {material.actual > 0 ? (
                  <>
                    {material.variancePercent > 0 ? '+' : ''}
                    {material.variancePercent.toFixed(1)}%
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td
                style={{
                  color: material.actual > 0 ? getVarianceColor(material.variancePercent) : '#666',
                  fontSize: '16px',
                  fontWeight: 'bold',
                }}
              >
                {material.actual > 0 ? getVarianceStatus(material.variancePercent) : '—'}
              </td>
            </tr>
          ))}
          <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 600 }}>
            <td style={{ textAlign: 'left' }}>Total</td>
            <td>{totals.planned.toFixed(2)}</td>
            <td>{totals.actual.toFixed(2)}</td>
            <td style={{ color: totals.difference >= 0 ? '#dc3545' : '#28a745' }}>
              {totals.difference > 0 ? '+' : ''}
              {totals.difference.toFixed(2)}
            </td>
            <td style={{ color: totals.difference >= 0 ? '#dc3545' : '#28a745' }}>
              {((totals.difference / totals.planned) * 100).toFixed(1)}%
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <div
        style={{
          marginTop: '12px',
          padding: '8px',
          backgroundColor: '#f5f5f5',
          fontSize: '11px',
          borderRadius: '4px',
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
        }}
      >
        <span style={{ fontWeight: 600 }}>Status:</span>
        <span style={{ color: '#28a745' }}>✓ Good (±0-5%)</span>
        <span style={{ color: '#ffc107' }}>△ Fair (±5-20%)</span>
        <span style={{ color: '#dc3545' }}>✗ High (±20%+)</span>
      </div>
    </div>
  );
};

export default MaterialUsageComparison;
